import { Flex, Image, Td, Text, Th, Tr } from '@chakra-ui/react';
import {
  BuyerOfferAnalysisFragment,
  KeyTermType,
} from '@client/graphql/__generated__/types';
import { format } from 'date-fns';
import isNil from 'lodash/isNil';
import { useNavigate } from 'react-router-dom';
import { BasicCardTable } from '~/common/components/BasicCardTable/BasicCardTable';
import { formatToCurrency } from '~/services/document/utils/number';
import {
  DEFAULT_PROPERTY_IMAGE_URL,
  getPropertySource,
} from '../../utils/listing.utils';
import { formatPartyConsumers } from '../../utils/party.utils';
import { UserAvatar } from '../User/UserAvatar';
import { OfferScoreChart } from './OfferScore/OfferScoreChart';

const formatPurchasePrice = (value?: number) => {
  if (isNil(value)) return;

  const notation = value >= 1_000_000 ? 'compact' : 'standard';

  // Properly format millions values with 2 decimal places
  // 1,950,000 should be 1.95M but 19,500,000 should be 19.5M
  const millionsValue = value / 1000000;
  const decimals =
    value >= 1000000
      ? millionsValue < 10
        ? 2
        : millionsValue < 100
          ? 1
          : 0
      : 0;

  return formatToCurrency(value, decimals, notation);
};

interface OfferScorecardsTableProps {
  offerScorecards: Partial<BuyerOfferAnalysisFragment>[];
  loading: boolean;
  fetchTriggerRef: (ref: HTMLDivElement) => void;
  showAgent?: boolean;
  showBuyer?: boolean;
}

export const OfferScorecardsTable = ({
  offerScorecards,
  loading,
  fetchTriggerRef,
  showAgent = true,
  showBuyer = true,
}: OfferScorecardsTableProps) => {
  const navigate = useNavigate();

  const body =
    !loading && !offerScorecards?.length ? (
      <Tr>
        <Td borderBottom="none" colSpan={100}>
          <Text fontSize="xl" fontWeight="medium" textAlign="center">
            No Offer Scorecards yet
          </Text>
        </Td>
      </Tr>
    ) : (
      <>
        {offerScorecards.map((scorecard) => {
          const purchasePriceRec = scorecard.agentOfferRecs?.keyTerms?.find(
            (rec) => rec.type === KeyTermType.PURCHASE_PRICE
          );

          const address = scorecard.listing?.mlsListing?.address;

          const isShared =
            scorecard.shareLinks && scorecard.shareLinks.length > 0;
          const buyerNames = scorecard.party
            ? formatPartyConsumers(scorecard.party)
            : '-';

          return (
            <Tr
              key={scorecard.id}
              _hover={{ bg: 'whiteAlpha.100' }}
              cursor="pointer"
              height={{ base: '60px', md: 'auto' }}
              onClick={() => navigate(`/offer-scorecard/${scorecard.id}`)}
            >
              {showAgent && (
                <Td whiteSpace="nowrap">
                  <Flex alignItems="center" gap={2} minWidth="max-content">
                    {scorecard.buyersAgent?.user && (
                      <UserAvatar size="sm" user={scorecard.buyersAgent.user} />
                    )}
                    <Text overflow="hidden" textOverflow="ellipsis">
                      {scorecard.buyersAgent?.user?.fullName}
                    </Text>
                  </Flex>
                </Td>
              )}

              <Td>
                <Flex alignItems="center" gap={2} minWidth="max-content">
                  <Image
                    alt="Property"
                    borderRadius="md"
                    fallbackSrc={DEFAULT_PROPERTY_IMAGE_URL}
                    height="35px"
                    objectFit="cover"
                    src={getPropertySource(
                      scorecard.listing?.mlsListing?.photos?.[0]
                    )}
                    width="50px"
                  />
                  <Flex direction="column">
                    <Text
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {address?.streetNumberText} {address?.streetName}
                      {address?.unit ? `, ${address.unit}` : ''}
                    </Text>
                    <Text
                      color="whiteAlpha.600"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {address?.city} {address?.state} {address?.postalCode}
                    </Text>
                  </Flex>
                </Flex>
              </Td>
              {showBuyer && (
                <Td>
                  <Flex alignItems="center" gap={2} minWidth="max-content">
                    <Text
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {buyerNames}
                    </Text>
                    {isShared && (
                      <Text
                        bg="whiteAlpha.200"
                        borderRadius="full"
                        color="white"
                        fontSize="sm"
                        letterSpacing="wider"
                        px={3}
                        py={1}
                        textTransform="uppercase"
                        whiteSpace="nowrap"
                      >
                        Shared
                      </Text>
                    )}
                  </Flex>
                </Td>
              )}
              <Td whiteSpace="nowrap">
                {purchasePriceRec?.value
                  ? formatPurchasePrice(purchasePriceRec.value)
                  : '-'}
              </Td>
              <Td>
                <OfferScoreChart
                  maskColor="opaque.indigo_800_whiteAlpha_100"
                  score={
                    scorecard.offerScore?.__typename === 'OfferScore'
                      ? scorecard.offerScore.value
                      : undefined
                  }
                  size="sm"
                />
              </Td>
              <Td whiteSpace="nowrap">
                {format(
                  new Date(scorecard.createdAt as string),
                  'MMM dd, yyyy'
                )}
              </Td>
            </Tr>
          );
        })}
      </>
    );

  return (
    <Flex
      basis={0}
      direction="column"
      grow={1}
      height="100%"
      minHeight={0}
      width="100%"
    >
      <BasicCardTable
        body={body}
        fetchTriggerRef={fetchTriggerRef}
        header={
          <Tr>
            {showAgent && <Th w={{ base: '120px', md: '15%' }}>Agent</Th>}
            <Th w={{ base: '250px', md: '30%' }}>Property</Th>
            {showBuyer && <Th w={{ base: '150px', md: '20%' }}>Buyer</Th>}
            <Th w={{ base: '120px', md: '15%' }}>Purchase Price</Th>
            <Th w={{ base: '100px', md: '10%' }}>Offer Score</Th>
            <Th w={{ base: '100px', md: '10%' }}>Date Created</Th>
          </Tr>
        }
        loading={loading}
      />
    </Flex>
  );
};
